import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, LinearProgress, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import Page from '../Page';
import PageHeader from './shared/PageHeader';
import RuleGroupBuilder from './RuleGroupBuilder';
import { listSegments, createSegment, deleteSegment, previewEligibility } from '../../utils/outreachApi';

const Segments = () => {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [rules, setRules] = useState([]);
  const [count, setCount] = useState(0);

  const load = () => listSegments().then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (rules.length === 0) { setCount(0); return; }
    const t = setTimeout(() => {
      previewEligibility(rules).then(({ data }) => setCount(data.count));
    }, 350);
    return () => clearTimeout(t);
  }, [JSON.stringify(rules)]);

  const onSave = async () => {
    await createSegment({ name, rules: rules.map(({ id, ...rest }) => rest), estimated_count: count });
    setOpen(false); setName(''); setRules([]); setCount(0);
    load();
  };

  return (
    <Page title="Outreach Segments | CRM">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <PageHeader
          title="Outreach Segments"
          subtitle="Reusable validation rule sets — apply to any campaign"
          actions={
            <Button variant="contained" startIcon={<Icon icon={plusFill} />} onClick={() => setOpen(true)}>
              New Segment
            </Button>
          }
        />

        {!rows ? <LinearProgress /> : rows.length === 0 ? (
          <Card sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">No segments yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Save common audience rule sets once and reuse them across campaigns.
            </Typography>
            <Button variant="contained" startIcon={<Icon icon={plusFill} />} onClick={() => setOpen(true)}>
              Create your first segment
            </Button>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {rows.map((s) => (
              <Grid item xs={12} md={6} lg={4} key={s.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          owner: {s.owner.name} · used in {s.used_in_campaigns.length} campaign(s)
                        </Typography>
                      </Box>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error"
                                    onClick={() => deleteSegment(s.id).then(load)}>
                          <Icon icon={trash2Fill} width={18} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      {s.estimated_count.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">estimated matches</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1.5 }}>
                      {s.rules.slice(0, 4).map((r, i) => (
                        <Chip key={i} size="small"
                              label={`${r.field} ${r.operator} ${Array.isArray(r.value) ? `[${r.value.join(',')}]` : r.value}`} />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create segment</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Segment name" fullWidth size="small"
                         value={name} onChange={(e) => setName(e.target.value)} />
              <RuleGroupBuilder rules={rules} onChange={setRules} />
              <Typography variant="body2" color="text.secondary">
                Live count: <strong>{count.toLocaleString()}</strong> companies match
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={!name || rules.length === 0} onClick={onSave}>Save</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default Segments;
